"use client";

import { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LambLogo from '@/components/lamb-logo';
import LanguageSwitcher from '@/components/language-switcher';
import { useLanguage } from '@/contexts/language-context';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const pathname = usePathname();
  const { t } = useLanguage();

  // Throttled scroll handler for better performance
  const updateScrollState = useCallback(() => {
    const currentScrollY = window.scrollY;
    
    // Set scrolled state for background
    const isScrolled = currentScrollY > 50;
    if (isScrolled !== scrolled) {
      setScrolled(isScrolled);
    }
    
    // Handle visibility based on scroll direction
    let shouldBeVisible = visible;
    
    if (currentScrollY < 10) {
      // Always show at top
      shouldBeVisible = true;
    } else if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
      // Scrolling down - hide navbar
      shouldBeVisible = false;
      if (isOpen) {
        setIsOpen(false); // Close mobile menu when hiding
      }
    } else if (currentScrollY < lastScrollY.current) {
      // Scrolling up - show navbar
      shouldBeVisible = true;
    }
    
    if (shouldBeVisible !== visible) {
      setVisible(shouldBeVisible);
    }
    
    lastScrollY.current = currentScrollY;
    ticking.current = false;
  }, [scrolled, visible, isOpen]);

  const handleScroll = useCallback(() => {
    if (!ticking.current) {
      requestAnimationFrame(updateScrollState);
      ticking.current = true;
    }
  }, [updateScrollState]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const navItems = [
    { href: '/', label: t('nav.home') },
    { href: '/about', label: t('nav.about') },
    { href: '/contact', label: t('nav.contact') },
  ];

  const handlePortfolioClick = useCallback(() => {
    window.open('https://demo.agnuscloud.com', '_blank');
  }, []);

  const handleMobileMenuToggle = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const handleMobileNavClick = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleMobilePortfolioClick = useCallback(() => {
    handlePortfolioClick();
    setIsOpen(false);
  }, [handlePortfolioClick]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 transform ${
        visible ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0'
      } ${
        scrolled 
          ? 'bg-deep-purple/90 backdrop-blur-lg shadow-lg border-b border-cloud-lavender/20' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link href="/" className="flex items-center space-x-3">
            <motion.div
              whileHover={{ scale: 1.1, rotate: 5 }}
              whileTap={{ scale: 0.95 }}
              className="relative"
            >
              <LambLogo className="w-10 h-10" />
            </motion.div>
            <motion.span 
              className="text-xl font-light text-cream"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              Agnus Cloud
            </motion.span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <div key={item.href}>
                <Link
                  href={item.href}
                  className={`relative px-3 py-2 text-sm font-light transition-all duration-300 ${
                    pathname === item.href
                      ? 'text-cloud-blue'
                      : 'text-cream hover:text-cloud-blue'
                  }`}
                >
                  {item.label}
                  
                  {/* Active indicator */}
                  {pathname === item.href && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cloud-blue to-cloud-lavender rounded-full" />
                  )}
                  
                  {/* Hover effect */}
                  <motion.div
                    className="absolute inset-0 bg-cloud-blue/10 rounded-lg"
                    initial={{ opacity: 0, scale: 0.8 }}
                    whileHover={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.2 }}
                  />
                </Link>
              </div>
            ))}
            
            {/* Portfolio Button */}
            <div>
              <Button
                onClick={handlePortfolioClick}
                size="sm"
                className="bg-gradient-to-r from-cloud-blue to-cloud-lavender hover:from-cloud-blue/90 hover:to-cloud-lavender/90 text-white px-4 py-2 text-sm font-light rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                {t('nav.features')}
                <ExternalLink className="w-3 h-3" />
              </Button>
            </div>
            
            {/* Language Switcher */}
            <div>
              <LanguageSwitcher />
            </div>
          </div>

          {/* Mobile menu button and language switcher */}
          <div className="md:hidden flex items-center space-x-2">
            <div>
              <LanguageSwitcher />
            </div>
            <div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMobileMenuToggle}
                className="p-2 text-cream hover:text-cloud-blue hover:bg-cloud-blue/10 transition-all duration-300"
              >
                <motion.div
                  animate={{ rotate: isOpen ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </motion.div>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && visible && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden bg-deep-purple/95 backdrop-blur-lg border-t border-cloud-lavender/20"
          >
            <div className="px-4 py-4 space-y-2">
              {navItems.map((item, index) => (
                <motion.div
                  key={item.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: index * 0.1, duration: 0.3 }}
                >
                  <Link
                    href={item.href}
                    onClick={handleMobileNavClick}
                    className={`block px-4 py-3 text-base font-light rounded-lg transition-all duration-300 ${
                      pathname === item.href
                        ? 'text-cloud-blue bg-cloud-blue/20 border border-cloud-blue/30'
                        : 'text-cream hover:text-cloud-blue hover:bg-cloud-blue/10'
                    }`}
                  >
                    <motion.div
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      {item.label}
                    </motion.div>
                  </Link>
                </motion.div>
              ))}
              
              {/* Mobile Portfolio Button */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ delay: navItems.length * 0.1, duration: 0.3 }}
              >
                <Button
                  onClick={handleMobilePortfolioClick}
                  className="w-full bg-gradient-to-r from-cloud-blue to-cloud-lavender hover:from-cloud-blue/90 hover:to-cloud-lavender/90 text-white px-4 py-3 text-base font-light rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                >
                  {t('nav.features')}
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;